import { useState, useCallback } from "react";

import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { currentFeatures } from "@/configs/Settings";

interface UseRelatedPersonsReturn {
  relatedPerson: Array<any> | undefined;
  patientRelated: Array<any> | undefined;
  guardianRelated: Array<any> | undefined;
  loading: boolean;
  fetchRelatedData: (patientData: any) => Promise<void>;
}

export const useRelatedPersons = ({
  identifier,
}: {
  identifier: string;
}): UseRelatedPersonsReturn => {
  const [relatedPerson, setRelatedPerson] = useState<Array<any>>();
  const [patientRelated, setPatientRelated] = useState<Array<any>>();
  const [guardianRelated, setGuardianRelated] = useState<Array<any>>();
  const [loading, setLoading] = useState(false);

  const fetchRelatedData = useCallback(async (patientData: any) => {
    if (!patientData) return;

    setLoading(true);
    try {
      const idsRelated = patientData?.link
        ?.map((el: any) => el?.other?.reference.split("/")[1])
        .join(",");

      // Busca guardiões
      const auxLinkGuardian = await GetRelatedPerson({
        patient: `${patientData?.id}`,
      }).then((response) => response?.data?.entry);

      if (
        auxLinkGuardian?.find((el: any) =>
          el.resource?.resourceType?.includes("RelatedPerson")
        )
      ) {
        const guardianData = currentFeatures?.defaultApp
          ? await GetRelatedPerson({
              patient: `${patientData?.id}`,
            }).then((response) => response?.data?.entry)
          : await GetRelatedPerson({
              identifier: `${patientData?.id?.split("-")[1]}`,
            }).then((response) => response?.data?.entry);
        setGuardianRelated(guardianData);
      }

      // Busca pessoas relacionadas com include do patient (pet)
      if (idsRelated) {
        const responseRelated = await GetRelatedPerson({
          identifier: patientData?.id?.split("-")[1],
          includePatient: true, // Inclui os dados dos patients (pets) na mesma requisição
          vetRelated: true,
          includePhoto: true,
        }).then((response) => response?.data?.entry);

        // Separa RelatedPersons e Patients da resposta
        const auxRelatedPerson = responseRelated?.filter(
          (e: any) => e?.resource.resourceType === "RelatedPerson"
        );
        setRelatedPerson(auxRelatedPerson);

        const auxPatients = responseRelated?.filter(
          (e: any) => e?.resource.resourceType === "Patient"
        );

        // Monta a lista de patients relacionados com informações do RelatedPerson
        const patientsRelated = auxPatients?.map((patientEntry: any) => {
          const patient = patientEntry.resource;
          // Encontra o RelatedPerson correspondente
          const auxRelated = auxRelatedPerson?.find((relatedEntry: any) => {
            return relatedEntry.resource?.patient?.reference?.includes(
              patient.id
            );
          })

          return {
            ...patient,
            name: [{ text: auxRelated?.resource?.patient?.display || '-', use: 'official' }],
            identifier: auxRelated?.resource?.patient?.reference?.split("/")[1],
            isVerify: auxRelated?.resource?.active,
            period: auxRelated?.resource?.period,
            relatedId: auxRelated?.resource?.id,
          };
        });
        setPatientRelated(patientsRelated);
      }
    } catch (error) {
      // toast.error("Erro ao carregar dados relacionados");
      console.error("Erro ao carregar dados relacionados:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    relatedPerson,
    patientRelated,
    guardianRelated,
    loading,
    fetchRelatedData,
  };
};
